import { createSignal, onMount } from "solid-js";
import { DenominationProps } from "~/components/denomination_grid/denominations_grid";
import { debounce } from "@solid-primitives/scheduled";
import { useDenominationGridState } from "./useDenominationGridState";
import { clientIdConfig } from "~/data/partner_config";

export type DenominationGridState = ReturnType<
  typeof useDenominationsGridDeprecated
>;

export function useDenominationsGridDeprecated(props: DenominationProps) {
  const internalState = useDenominationGridState(props);

  function enoughCoinsAvailable() {
    if (!props.isCoinsOnly) {
      return true;
    }

    return props.coinsBalance >= (internalState.dueAmount() ?? 0);
  }

  const isFixedDenominationBrand =
    props.brand.amountConditions.denominations != null;

  const customAmountDefaultDenominationsList = [
    100, 500, 1000, 2000, 5000,
  ].filter(
    (denomination) =>
      denomination >= props.brand.amountConditions.minAmount &&
      denomination <= props.brand.amountConditions.maxAmount
  );

  let defaultAmount = props.brand.amountConditions.defaultAmount;
  if (props.getClientId() === clientIdConfig.tdClientId) {
    defaultAmount = props.brand.amountConditions.minAmount;
  }

  const customAmountInputDebounce = debounce((customAmount: number) => {
    if (isNaN(customAmount)) return;
    internalState.setSelectedAmount(customAmount);
  }, 300);

  const isVariantBrand = props.brand.variantDetailedResponse != null;

  let isCustomDenominationAllowed =
    !isFixedDenominationBrand && !isVariantBrand;

  let denominationList =
    props.denominationList ??
    props.brand.amountConditions.denominations ??
    customAmountDefaultDenominationsList;

  if (props.isCoinsOnly) {
    denominationList = filterOutUnnecessaryDenominations({
      coinsBalance: props.coinsBalance,
      denominations: denominationList,
      minRedemptionAmount: props.brand.amountConditions.minAmount,
    });

    if (props.coinsBalance > 0 && props.coinsBalance < defaultAmount) {
      defaultAmount = props.coinsBalance;
    }
  }

  function safeToShowOnlyOneDenomination(coinsBalance: number) {
    if (!props.isCoinsOnly) {
      return false;
    }

    if (isFixedDenominationBrand || isVariantBrand) {
      return false;
    }

    if (coinsBalance < props.minAmount || coinsBalance > props.maxAmount) {
      return false;
    }

    return true;
  }

  let columnsCount = denominationList.length <= 4 ? 2 : 3;
  if (props.getClientId() === clientIdConfig.tdClientId) {
    // td client is forced to show 3 columns
    columnsCount = 3;
  }

  // if the default amount is present and not avaliable in the denominationList then show it in the custom input field
  const defaultAmountInCustomInput =
    isCustomDenominationAllowed && !denominationList.includes(defaultAmount)
      ? defaultAmount
      : null;

  function filterOutUnnecessaryDenominations(data: {
    coinsBalance: number;
    denominations: number[];
    minRedemptionAmount: number;
  }) {
    if (data.coinsBalance < data.minRedemptionAmount) {
      return data.denominations;
    }

    // filter out denominations greater than available coins
    return data.denominations.filter(
      (denomination) => denomination <= data.coinsBalance
    );
  }

  onMount(() => {
    if (defaultAmountInCustomInput !== null) {
      setCustomSelected(true);
      document.getElementById("custom-amount-input")?.focus();
      internalState.setSelectedAmount(defaultAmountInCustomInput);
    }
    if (props.isCoinsOnly && !internalState.selectedAmount()) {
      internalState.setSelectedAmount(defaultAmount);
    }
  });

  const [customSelected, setCustomSelected] = createSignal<boolean>(
    isCustomDenominationAllowed &&
      customAmountDefaultDenominationsList.length == 0
  );

  const denominationBorderClass =
    "text-textDark group select-none darkTheme:border-baseLight relative flex max-h-10 dark:bg-basePrimaryDark darkTheme:hover:border-[#7c7c7c] darkTheme:bg-baseTertiaryDark  dark:text-baseTertiaryLight  cursor-pointer flex-row items-center justify-center rounded-lg px-2.5 py-2 hover:border-baseSecondaryMedium   dark:hover:border-baseTertiaryLight lg:h-[48px] lg:py-3.5";

  return {
    customSelected,
    setCustomSelected,
    customAmountInputDebounce,
    isFixedDenominationBrand,
    customAmountDefaultDenominationsList,
    defaultAmount,
    isVariantBrand,
    externalProps: props,
    enoughCoinsAvailable,
    columnsCount,
    defaultAmountInCustomInput,
    safeToShowOnlyOneDenomination,
    denominationList,
    isCustomDenominationAllowed,
    denominationBorderClass,
    internalState,
  };
}

export default useDenominationsGridDeprecated;
